/**
 * Bank account linking consent, on the DS ConfirmDialog.
 *
 * Owns its copy rather than taking it as props, which is what the popover it
 * replaced did: both call sites asked for the same title, body and icon, and
 * passing the strings in is what let one caller translate before handing them
 * over while the other passed raw keys.
 */
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, Typography } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { BankIcon } from "@hugeicons-pro/core-stroke-rounded";
import { ConfirmDialog } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import { useBrandingContext } from "context/BrandingContext";

// Stripe's account linking disclosure rather than an Outpave marketing page:
// it stands in for any platform that configures no terms URL of its own.
const ACCOUNT_LINKING_TERMS_URL = "https://outpave.com/account-linking-terms";

interface BankTermsDialogProps {
  open: boolean;
  /** Reports the answer back: accepting authorizes the debit and credit setup. */
  onClose: (accepted: boolean) => void;
}

const BankTermsDialog: React.FC<BankTermsDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { platform } = useBrandingContext();

  const termsUrl = platform?.termsUrl || ACCOUNT_LINKING_TERMS_URL;

  return (
    <ConfirmDialog
      open={open}
      title={t("Do you accept the terms?")}
      confirmLabel={t("Accept")}
      cancelLabel={t("Cancel")}
      tone="primary"
      icon={
        <HugeiconsIcon
          icon={BankIcon}
          size={18}
          data-testid="bank-terms-glyph"
        />
      }
      onConfirm={() => onClose(true)}
      onCancel={() => onClose(false)}
      message={
        <Stack gap="var(--space-4)">
          {t("AcceptTermsInitiateDebitAndCreditDescription")}
          <Typography
            variant="caption"
            sx={{ color: "var(--color-text-tertiary)" }}
          >
            <Trans
              i18nKey="termsAgreement"
              components={{
                // The children are a fallback: Trans replaces them with the
                // linked span of the translation.
                termsLink: (
                  <Link
                    href={termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    terms and conditions
                  </Link>
                ),
              }}
            />
          </Typography>
        </Stack>
      }
    />
  );
};

export default BankTermsDialog;
